import { DialogRef } from '@angular/cdk/dialog';
import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ResponsiveService, setFormErrors } from '@cf/core';
import { TranslateCutPipe } from '@cf/features/i18n';
import type { CfDialogRef } from '@cf/shared/dialog';
import {
  CfDialogContentDirective,
  CfDialogDismissDirective,
  CfDialogService,
} from '@cf/shared/dialog';
import {
  FormFieldComponent,
  InputDirective,
  InputErrorComponent,
  LabelComponent,
} from '@cf/shared/forms/form-field';
import { ButtonDirective, ButtonLoadingComponent } from '@cf/shared/ui/button';
import { CfIconDirective } from '@cf/shared/ui/icons';
import { ConnectionService } from '@cf/temp/services';
import { NgxTolgeeModule } from '@tolgee/ngx';
import { ICloudHintComponent } from '../icloud-hint/icloud-hint.component';

@Component({
  selector: 'cf-connect-icloud-dialog',
  imports: [
    CommonModule,
    CfDialogContentDirective,
    CfDialogDismissDirective,
    FormFieldComponent,
    LabelComponent,
    NgxTolgeeModule,
    InputDirective,
    InputErrorComponent,
    ReactiveFormsModule,
    TranslateCutPipe,
    ButtonDirective,
    ButtonLoadingComponent,
    CfIconDirective,
  ],
  templateUrl: './connect-icloud-dialog.component.html',
})
export class ConnectIcloudDialogComponent {
  #cfDialog = inject(CfDialogService);
  #connectionService = inject(ConnectionService);
  #destroyRef = inject(DestroyRef);
  #responsiveService = inject(ResponsiveService);
  isMobile = toSignal(this.#responsiveService.isMobile$);

  ref = inject(DialogRef<boolean, ConnectIcloudDialogComponent>);

  loading = signal(false);
  customError = signal('');
  form = new FormGroup({
    email: new FormControl('', [Validators.required]),
    password: new FormControl('', [Validators.required]),
  });

  #hintDialog?: CfDialogRef<ICloudHintComponent>;

  constructor() {
    inject(DestroyRef).onDestroy(() => {
      this.#hintDialog?.dismiss();
    });
  }

  openHint(event: Event) {
    event.preventDefault();
    if (this.#hintDialog) {
      return;
    }
    this.#hintDialog = this.#cfDialog.open({
      content: ICloudHintComponent,
      mode: 'sheet-right',
      modal: this.isMobile() ? true : false,
    });
    this.#hintDialog.closed$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
      this.#hintDialog = undefined;
    });
  }

  connect() {
    this.customError.set('');
    if (!this.form.valid) {
      this.form.markAllAsTouched();
      return;
    }
    this.loading.set(true);
    const value = this.form.value;
    this.#connectionService
      .appleConnect({
        email: value.email!,
        password: value.password!,
      })
      .subscribe({
        next: () => {
          this.loading.set(false);
          this.ref.close(true);
        },
        error: (res) => {
          setFormErrors(this.form, res);
          if (res.error.errors.non_field_errors) {
            this.customError.set(res.error.errors.non_field_errors[0].message);
          }
          this.loading.set(false);
        },
      });
  }
}
